// src/components/ViewPersonnelDialog.js
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  Divider,
  Chip,
} from '@mui/material';
import {
  CheckCircle as VerifiedIcon,
  Cancel as NotVerifiedIcon,
} from '@mui/icons-material';

const ViewPersonnelDialog = ({ open, personnel, onClose }) => {
  if (!personnel) return null;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'VERIFIED':
        return 'success';
      case 'NOT_VERIFIED':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    return status === 'VERIFIED' ? <VerifiedIcon /> : <NotVerifiedIcon />;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6">Personnel Details</Typography>
      </DialogTitle>
      <DialogContent dividers>
        {/* Status Badge */}
        <Chip
          icon={getStatusIcon(personnel.verificationStatus)}
          label={personnel.verificationStatus.replace('_', ' ')}
          color={getStatusColor(personnel.verificationStatus)}
          sx={{ mb: 3, fontWeight: 600 }}
        />

        {/* Personal Information */}
        <Typography variant="subtitle1" fontWeight="700" sx={{ mb: 1, color: 'primary.main' }}>
          PERSONAL INFORMATION
        </Typography>
        <Table size="small" sx={{ mb: 3, border: '1px solid', borderColor: 'grey.300' }}>
          <TableBody>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200', width: '30%', border: '1px solid', borderColor: 'grey.300' }}>
                Service Number
              </TableCell>
              <TableCell sx={{ border: '1px solid', borderColor: 'grey.300' }}>
                {personnel.serviceNumber}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200', border: '1px solid', borderColor: 'grey.300' }}>
                IPPIS Number
              </TableCell>
              <TableCell sx={{ border: '1px solid', borderColor: 'grey.300' }}>
                {personnel.ippisNumber}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200', border: '1px solid', borderColor: 'grey.300' }}>
                Full Name
              </TableCell>
              <TableCell sx={{ border: '1px solid', borderColor: 'grey.300' }}>
                {personnel.firstName} {personnel.middleName} {personnel.lastName}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200', border: '1px solid', borderColor: 'grey.300' }}>
                Gender
              </TableCell>
              <TableCell sx={{ border: '1px solid', borderColor: 'grey.300' }}>
                {personnel.gender}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200', border: '1px solid', borderColor: 'grey.300' }}>
                Date of Birth
              </TableCell>
              <TableCell sx={{ border: '1px solid', borderColor: 'grey.300' }}>
                {formatDate(personnel.birthDate)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>

        {/* Employment Information */}
        <Typography variant="subtitle1" fontWeight="700" sx={{ mb: 1, color: 'primary.main' }}>
          EMPLOYMENT INFORMATION
        </Typography>
        <Table size="small" sx={{ mb: 3, border: '1px solid', borderColor: 'grey.300' }}>
          <TableBody>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200', width: '30%', border: '1px solid', borderColor: 'grey.300' }}>
                Rank
              </TableCell>
              <TableCell sx={{ border: '1px solid', borderColor: 'grey.300' }}>
                {personnel.rankName}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200', border: '1px solid', borderColor: 'grey.300' }}>
                Grade
              </TableCell>
              <TableCell sx={{ border: '1px solid', borderColor: 'grey.300' }}>
                {personnel.gradeName} {personnel.gradeLevel}/{personnel.gradeStep}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200', border: '1px solid', borderColor: 'grey.300' }}>
                Date of First Appointment
              </TableCell>
              <TableCell sx={{ border: '1px solid', borderColor: 'grey.300' }}>
                {formatDate(personnel.dateOfFirstAppointment)}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200', border: '1px solid', borderColor: 'grey.300' }}>
                Expected Retirement
              </TableCell>
              <TableCell sx={{ border: '1px solid', borderColor: 'grey.300' }}>
                {formatDate(personnel.expectedRetirementDate)}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200', border: '1px solid', borderColor: 'grey.300' }}>
                Command
              </TableCell>
              <TableCell sx={{ border: '1px solid', borderColor: 'grey.300' }}>
                {personnel.command?.code} - {personnel.command?.name}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200', border: '1px solid', borderColor: 'grey.300' }}>
                Zone
              </TableCell>
              <TableCell sx={{ border: '1px solid', borderColor: 'grey.300' }}>
                {personnel.command?.zone}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200', border: '1px solid', borderColor: 'grey.300' }}>
                State
              </TableCell>
              <TableCell sx={{ border: '1px solid', borderColor: 'grey.300' }}>
                {personnel.command?.state}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>

        {/* Banking Information */}
        <Typography variant="subtitle1" fontWeight="700" sx={{ mb: 1, color: 'primary.main' }}>
          BANKING INFORMATION
        </Typography>
        <Table size="small" sx={{ mb: 3, border: '1px solid', borderColor: 'grey.300' }}>
          <TableBody>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200', width: '30%', border: '1px solid', borderColor: 'grey.300' }}>
                Bank Name
              </TableCell>
              <TableCell sx={{ border: '1px solid', borderColor: 'grey.300' }}>
                {personnel.bankName}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200', border: '1px solid', borderColor: 'grey.300' }}>
                Account Number
              </TableCell>
              <TableCell sx={{ border: '1px solid', borderColor: 'grey.300' }}>
                {personnel.accountNumber}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200', border: '1px solid', borderColor: 'grey.300' }}>
                PFA Name
              </TableCell>
              <TableCell sx={{ border: '1px solid', borderColor: 'grey.300' }}>
                {personnel.pfaName}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200', border: '1px solid', borderColor: 'grey.300' }}>
                PENCOM ID
              </TableCell>
              <TableCell sx={{ border: '1px solid', borderColor: 'grey.300' }}>
                {personnel.pencomId}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>

        {/* Contact Information */}
        {(personnel.phoneNumber || personnel.email) && (
          <>
            <Typography variant="subtitle1" fontWeight="700" sx={{ mb: 1, color: 'primary.main' }}>
              CONTACT INFORMATION
            </Typography>
            <Table size="small" sx={{ mb: 3, border: '1px solid', borderColor: 'grey.300' }}>
              <TableBody>
                {personnel.phoneNumber && (
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200', width: '30%', border: '1px solid', borderColor: 'grey.300' }}>
                      Phone Number
                    </TableCell>
                    <TableCell sx={{ border: '1px solid', borderColor: 'grey.300' }}>
                      {personnel.phoneNumber}
                    </TableCell>
                  </TableRow>
                )}
                {personnel.email && (
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200', border: '1px solid', borderColor: 'grey.300' }}>
                      Email Address
                    </TableCell>
                    <TableCell sx={{ border: '1px solid', borderColor: 'grey.300' }}>
                      {personnel.email}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </>
        )}

        {/* Verification Information */}
        {personnel.verification && (
          <>
            <Divider sx={{ my: 3 }} />
            <Typography variant="subtitle1" fontWeight="700" sx={{ mb: 1, color: 'success.main' }}>
              VERIFICATION DETAILS
            </Typography>
            <Table size="small" sx={{ border: '1px solid', borderColor: 'grey.300' }}>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200', width: '30%', border: '1px solid', borderColor: 'grey.300' }}>
                    Reference Number
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', border: '1px solid', borderColor: 'grey.300' }}>
                    {personnel.verification.referenceNumber}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200', border: '1px solid', borderColor: 'grey.300' }}>
                    Verified At
                  </TableCell>
                  <TableCell sx={{ border: '1px solid', borderColor: 'grey.300' }}>
                    {new Date(personnel.verification.verifiedAt).toLocaleString('en-GB')}
                  </TableCell>
                </TableRow>
                {personnel.verification.verifiedBy && (
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.200', border: '1px solid', borderColor: 'grey.300' }}>
                      Verified By
                    </TableCell>
                    <TableCell sx={{ border: '1px solid', borderColor: 'grey.300' }}>
                      {personnel.verification.verifiedBy.firstName}{' '}
                      {personnel.verification.verifiedBy.lastName}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewPersonnelDialog;